import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "statusMessage",
    "clockInForm",
    "clockOutForm",
    "clockInLatitude",
    "clockInLongitude",
    "clockInAccuracy",
    "clockOutLatitude",
    "clockOutLongitude",
    "clockOutAccuracy",
    "clockInButton",
    "clockOutButton"
  ]

  connect() {
    this.initialClockInDisabled = this.hasClockInButtonTarget ? this.clockInButtonTarget.disabled : false
    this.initialClockOutDisabled = this.hasClockOutButtonTarget ? this.clockOutButtonTarget.disabled : false
  }

  clockIn(event) {
    this.captureAndSubmit(event, "clockIn")
  }

  clockOut(event) {
    this.captureAndSubmit(event, "clockOut")
  }

  async captureAndSubmit(event, actionType) {
    event.preventDefault()

    if (!navigator.geolocation) {
      this.showStatus("Geolocation is not supported by this browser.", true)
      return
    }

    this.disableActionButton(actionType)
    this.showStatus("Capturing precise GPS location...")

    try {
      const position = await this.getPositionWithFallback()
      this.assignCoordinates(actionType, position.coords)
      this.showStatus("Location captured. Submitting clock entry...")
      this.formFor(actionType).requestSubmit()
    } catch (error) {
      this.restoreActionButton(actionType)
      this.showStatus(this.geolocationErrorMessage(error), true)
    }
  }

  assignCoordinates(actionType, coords) {
    this.latitudeTargetFor(actionType).value = coords.latitude.toFixed(6)
    this.longitudeTargetFor(actionType).value = coords.longitude.toFixed(6)
    this.accuracyTargetFor(actionType).value = Number.isFinite(coords.accuracy) ? coords.accuracy.toFixed(2) : ""
  }

  formFor(actionType) {
    return actionType === "clockIn" ? this.clockInFormTarget : this.clockOutFormTarget
  }

  latitudeTargetFor(actionType) {
    return actionType === "clockIn" ? this.clockInLatitudeTarget : this.clockOutLatitudeTarget
  }

  longitudeTargetFor(actionType) {
    return actionType === "clockIn" ? this.clockInLongitudeTarget : this.clockOutLongitudeTarget
  }

  accuracyTargetFor(actionType) {
    return actionType === "clockIn" ? this.clockInAccuracyTarget : this.clockOutAccuracyTarget
  }

  disableActionButton(actionType) {
    const button = actionType === "clockIn" ? this.clockInButtonTarget : this.clockOutButtonTarget
    button.disabled = true
    button.dataset.originalText = button.textContent
    button.textContent = "Locating..."
  }

  restoreActionButton(actionType) {
    const button = actionType === "clockIn" ? this.clockInButtonTarget : this.clockOutButtonTarget
    const originalDisabled = actionType === "clockIn" ? this.initialClockInDisabled : this.initialClockOutDisabled

    button.disabled = originalDisabled
    button.textContent = button.dataset.originalText || button.textContent
  }

  geolocationErrorMessage(error) {
    if (error.code === "LOW_ACCURACY") {
      const bestAccuracyText = Number.isFinite(error.bestAccuracy) ? ` Best was ~${Math.round(error.bestAccuracy)}m.` : ""
      return `Could not capture a usable location.${bestAccuracyText} Enable location access, move near a window, and try again.`
    }

    const permissionDeniedCode = 1
    const positionUnavailableCode = 2
    const timeoutCode = 3

    switch (error.code) {
      case permissionDeniedCode:
        return "Location permission was denied. Enable GPS access and try again."
      case positionUnavailableCode:
        return "Current location is unavailable. Turn on device GPS and try again."
      case timeoutCode:
        return "GPS request timed out before a precise fix. Move outdoors and try again."
      default:
        return "Unable to capture your location. Please try again."
    }
  }

  async getPositionWithFallback() {
    return this.requestBestHighAccuracyPosition()
  }

  requestBestHighAccuracyPosition() {
    return new Promise((resolve, reject) => {
      let bestPosition = null
      let watchId = null
      let timeoutId = null
      let settled = false

      const finish = (callback, value) => {
        if (settled) return
        settled = true

        if (watchId !== null) navigator.geolocation.clearWatch(watchId)
        if (timeoutId) clearTimeout(timeoutId)

        callback(value)
      }

      const captureTimeoutMs = this.captureTimeoutMs()

      watchId = navigator.geolocation.watchPosition(
        (position) => {
          const accuracy = Number(position.coords.accuracy)
          if (Number.isFinite(accuracy)) {
            if (!bestPosition || accuracy < Number(bestPosition.coords.accuracy)) {
              bestPosition = position
              const quality = accuracy <= 30 ? "GPS" : accuracy <= 100 ? "good" : "approximate"
              this.showStatus(`Locating… accuracy ~${Math.round(accuracy)}m (${quality})`)
            }

            if (accuracy <= this.targetAccuracyMeters()) {
              finish(resolve, position)
            }
          }
        },
        (error) => {
          const permissionDeniedCode = 1
          if (error.code === permissionDeniedCode) {
            finish(reject, error)
          }
        },
        {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: captureTimeoutMs
        }
      )

      timeoutId = setTimeout(() => {
        const bestAccuracy = Number(bestPosition?.coords?.accuracy)
        const hasAcceptableAccuracy = bestPosition && Number.isFinite(bestAccuracy) && bestAccuracy <= this.maxAcceptedAccuracyMeters()

        if (hasAcceptableAccuracy) {
          finish(resolve, bestPosition)
          return
        }

        const lowAccuracyError = new Error("GPS accuracy too low")
        lowAccuracyError.code = "LOW_ACCURACY"
        if (Number.isFinite(bestAccuracy)) {
          lowAccuracyError.bestAccuracy = bestAccuracy
        }

        finish(reject, lowAccuracyError)
      }, captureTimeoutMs)
    })
  }

  targetAccuracyMeters() {
    // Resolve immediately once GPS is this good (phones/tablets with real GPS)
    return 30
  }

  maxAcceptedAccuracyMeters() {
    return 30
  }

  captureTimeoutMs() {
    return 25000
  }

  showStatus(message, isError = false) {
    if (!this.hasStatusMessageTarget) return

    this.statusMessageTarget.textContent = message
    this.statusMessageTarget.classList.toggle("error", isError)
  }
}
